import React from "react";
import { Mail, MapPin, Instagram } from "lucide-react";

const PolaroidImage = ({ src, rotate, tapeColor, tapeRotate, caption, testId, className = "" }) => (
  <div
    data-testid={testId}
    className={`relative bg-[#fefdf8] p-3 pb-9 w-[180px] sm:w-[200px] md:w-[220px] shadow-[10px_14px_24px_rgba(0,0,0,0.18)] ${className}`}
    style={{ transform: `rotate(${rotate}deg)` }}
  >
    <span
      className="absolute -top-3 left-1/2 w-20 h-5 opacity-90"
      style={{
        background: tapeColor,
        transform: `translateX(-50%) rotate(${tapeRotate}deg)`,
      }}
      aria-hidden="true"
    />
    <img src={src} alt="" className="w-full aspect-[4/5] object-cover" />
    {caption && (
      <div className="absolute bottom-1.5 left-0 right-0 text-center font-hand text-[18px] sm:text-[20px] text-[#1a1a1a] leading-tight px-2">
        {caption}
      </div>
    )}
  </div>
);

const PolaroidVideo = ({ src, rotate, tapeColor, tapeRotate, caption, testId }) => (
  <div
    data-testid={testId}
    className="relative bg-[#fefdf8] p-3 pb-9 w-[180px] sm:w-[200px] md:w-[220px] shadow-[10px_14px_24px_rgba(0,0,0,0.18)]"
    style={{ transform: `rotate(${rotate}deg)` }}
  >
    <span
      className="absolute -top-3 left-1/2 w-20 h-5 opacity-90"
      style={{
        background: tapeColor,
        transform: `translateX(-50%) rotate(${tapeRotate}deg)`,
      }}
      aria-hidden="true"
    />
    <video
      src={src}
      data-testid={`${testId}-video`}
      autoPlay
      loop
      muted
      playsInline
      preload="auto"
      className="w-full aspect-[4/5] object-cover bg-black"
    />
    {caption && (
      <div className="absolute bottom-1.5 left-0 right-0 text-center font-hand text-[18px] sm:text-[20px] text-[#1a1a1a] leading-tight px-2">
        {caption}
      </div>
    )}
  </div>
);

/**
 * ContactBlock — cream editorial slab that lives BELOW the recent
 * projects rail. It keeps the "Sound is 50% of the picture" quote as
 * a lead-in and doubles as the contact card (email, location, socials).
 */
export const QuoteBand = () => {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#f4f1ea] text-[#1a1a1a] overflow-hidden"
    >
      {/* Subtle paper grain */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(rgba(0,0,0,0.6) 1px, transparent 1px)",
          backgroundSize: "3px 3px",
        }}
      />

      <div className="relative mx-auto max-w-[1400px] px-5 sm:px-6 md:px-10 py-16 md:py-24 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-3 flex justify-center lg:justify-start order-2 lg:order-1">
          <PolaroidImage
            testId="contact-polaroid-left"
            src="/ty-profile.png"
            rotate={-4}
            tapeColor="#f5e26a"
            tapeRotate={-8}
            caption="Ty"
          />
        </div>

        <div className="lg:col-span-6 text-center order-1 lg:order-2">
          <h2
            data-testid="contact-headline"
            className="font-display font-black text-3xl sm:text-4xl md:text-[44px] lg:text-[52px] leading-[1.05] tracking-[0.02em]"
          >
            SOUND IS <span className="text-[#e8472b]">50%</span>
            <br />
            OF THE PICTURE.
          </h2>

          {/* Contact details */}
          <div
            data-testid="contact-details"
            className="mt-10 flex flex-col items-center justify-center gap-5"
          >
            <a
              href="mailto:tyrelle@tyrellesound.com"
              data-testid="contact-email"
              className="group inline-flex items-center gap-3 font-display font-normal text-[22px] sm:text-[26px] md:text-[30px] tracking-tight text-[#1a1a1a] hover:text-[#e8472b] transition-colors"
            >
              <Mail size={26} strokeWidth={1.75} className="opacity-80 group-hover:opacity-100" />
              tyrelle@tyrellesound.com
            </a>

            <span
              data-testid="contact-location"
              className="inline-flex items-center gap-3 font-display font-normal text-[22px] sm:text-[26px] md:text-[30px] tracking-tight text-[#1a1a1a]"
            >
              <MapPin size={26} strokeWidth={1.75} className="opacity-80" />
              Kelso, WA
            </span>

            <a
              href="https://www.instagram.com/tyrellemassey/"
              target="_blank"
              rel="noreferrer noopener"
              data-testid="contact-instagram"
              className="group inline-flex items-center gap-3 font-display font-normal text-[22px] sm:text-[26px] md:text-[30px] tracking-tight text-[#1a1a1a] hover:text-[#e8472b] transition-colors"
            >
              <Instagram size={26} strokeWidth={1.75} className="opacity-80 group-hover:opacity-100" />
              @tyrellemassey
            </a>
          </div>
        </div>

        <div className="lg:col-span-3 flex justify-center lg:justify-end order-3">
          <PolaroidVideo
            testId="contact-polaroid-right"
            src="/edit-room-loop.mp4"
            rotate={4}
            tapeColor="#ff5fa2"
            tapeRotate={5}
            caption="Our edit room"
          />
        </div>
      </div>
    </section>
  );
};

export default QuoteBand;
